import {Controller} from '@hotwired/stimulus'
import Sortable from 'sortablejs'

// Duration to suppress morphing refreshes after a drag completes (ms).
// This prevents a race condition where a Pusher-driven morph arrives before
// the status-change API call finishes, causing the project card to appear
// in both the old and new columns (GF-1909).
const DRAG_COOLDOWN_MS = 5000

export default class extends Controller {
  connect() {
    this.sortables = []
    this.dragCooldownTimer = null
    const groupName = this.element.dataset.kanbanGroup || 'kanban'
    const allowReorder = this.element.dataset.allowReorder === 'true'
    this.element.querySelectorAll('.cards').forEach((col) => {
      const sortable = new Sortable(col, {
        group: groupName,
        // sort controls within-list reordering; cross-list drag is governed by `group`
        // and stays enabled even when sort: false.
        sort: allowReorder,
        animation: 150,
        handle: '.js-drag-handle',
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        onEnd: (evt) => {
          const item = evt.item
          const projectId = item.dataset.projectId
          const newStatus = evt.to.dataset.status
          const organizationId = this.element.dataset.organizationId
          const sortContext = this.element.dataset.sortContext || 'kanban_person'

          // Set a cooldown flag on the containing turbo frame to suppress morphing
          // refreshes while the API call is in flight. This prevents a race where
          // a morph with stale data duplicates the dragged card.
          this.startDragCooldown()

          const url = `/organizations/${organizationId}/work_items/update_kanban_items`

          const payload = {
            work_item_dom_id: projectId,
            organization_id: organizationId,
            sort_context: sortContext,
            project: {
              status: newStatus,
            },
          }

          if (allowReorder) {
            const orderedDomIds = Array.from(this.element.querySelectorAll('.js-drag-handle')).map((el) => el.dataset.projectId)
            payload.ordered_dom_ids = orderedDomIds
          }

          fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
            },
            body: JSON.stringify(payload),
          }).then((response) => {
            if (!response.ok)
              console.error('Failed to update') // eslint-disable-line no-console
            else
              console.log('Updated successfully') // eslint-disable-line no-console
          })
        },
      })
      this.sortables.push(sortable)
    })
  }

  // Set a data attribute on the turbo frame to signal pusherStreams.js that a
  // drag just completed and morphing refreshes should be suppressed.
  startDragCooldown() {
    const turboFrame = this.element.closest('turbo-frame') || this.element.closest('[id="content-frame"]')
    if (turboFrame) {
      turboFrame.setAttribute('data-drag-cooldown', 'true')

      if (this.dragCooldownTimer)
        clearTimeout(this.dragCooldownTimer)
      this.dragCooldownTimer = setTimeout(() => {
        turboFrame.removeAttribute('data-drag-cooldown')
        this.dragCooldownTimer = null
      }, DRAG_COOLDOWN_MS)
    }
  }

  disconnect() {
    if (this.sortables)
      this.sortables.forEach((sortable) => sortable.destroy())

    if (this.dragCooldownTimer)
      clearTimeout(this.dragCooldownTimer)
  }
}
